/* import __COLOCATED_TEMPLATE__ from './seller-posting-feedback-form.hbs'; */
import { bool, readOnly } from '@ember/object/computed';
import { computed } from '@ember/object';
import Component from '@ember/component';
import { guidFor } from '@ember/object/internals';
import { handleFormErrorOrModal } from 'sh-www/utils/handles-errors';
import { ASSIGNMENT } from 'sh-www/models/assignment';

const REASON_FORM_FIELDS = [
  'reason_availability',
  'reason_location',
  'reason_rate',
  'reason_services',
  'reason_preference',
  'reason_client',
  'reason_other',
];

const FORM_FIELDS = [
  ...REASON_FORM_FIELDS,
  'not_interested_text',
  'suggested_rate_amount',
  'suggested_rate_type'
];

const feedbackOption = (key, id) => ({
  key,
  model: { id },
});

const SELLER_POSTING_FEEDBACK_RATE_TYPE_OPTIONS = [
  feedbackOption('Flat', ASSIGNMENT.RATE_TYPE.FLAT),
  feedbackOption('Daily', ASSIGNMENT.RATE_TYPE.DAY),
];

/** @module */
const SellerPostingFeedbackForm = Component.extend({
  /** @inheritDoc */
  tagName: '',
  SELLER_POSTING_FEEDBACK_RATE_TYPE_OPTIONS,

  /** @type {boolean} */
  submitting: false,

  /** @type {Object} */
  formError: computed(`errors.{${FORM_FIELDS.join(',')}}`, function() {
    return Object.values(this.get('errors'))[0] || undefined;
  }),

  /** @type {boolean} */
  hasError: bool('formError'),

  assignmentRateType: 1,

  selectedAssignmentRateTypeLabel: computed('formData.suggested_rate_type', function() {
    const selected_rate_type = this.formData.suggested_rate_type;
    const rateType = SELLER_POSTING_FEEDBACK_RATE_TYPE_OPTIONS.find((rateType) => {
      return rateType.model.id === selected_rate_type;
    });

    return rateType.key;
  }),

  isRateFeedbackVisible: readOnly('formData.reason_rate'),

  // Requires at least one reason to be selected for Submit to be enabled
  disabledSubmitButton: computed(`formData.{${REASON_FORM_FIELDS.join(',')}}`, function() {
    return REASON_FORM_FIELDS.every(key => !this.formData?.[key]);
  }),

  /** @inheritDoc */
  init() {
    this._super(...arguments);
    this.set('inlineId', guidFor(this));
    this.set('errors', { });
    this.set('formData', {
      reason_other: this.data?.reasonOther || false,
      reason_availability: this.data?.reasonAvailability || false,
      reason_location: this.data?.reasonLocation || false,
      reason_rate: this.data?.reasonRate || false,
      reason_services: this.data?.reasonServices || false,
      reason_preference: this.data?.reasonPreference || false,
      reason_client: this.data?.reasonClient || false,
      not_interested_text: this.data?.notInterestedText || '',
      suggested_rate_amount: this.data?.suggestedRateAmount || null,
      suggested_rate_type: this.data?.suggestedRateType || this.assignmentRateType,
    });
  },

  /** @inheritDoc */
  actions: {
    /**
     * @param {Event} e
     */
    preventSubmit(e) {
      e.preventDefault();
    },

    /**
     * @param {string} prop
     */
    toggleEnum(prop) {
      const value = this.toggleProperty(`formData.${prop}`);

      this.set(`errors.${prop}`, undefined);

      if ((prop === 'reason_other')) {
        if (value) {
          document.getElementById(this.inlineId).focus();
        } else {
          this.setProperties({ 'formData.not_interested_text': '', 'errors.not_interested_text': undefined });
        }
      }
    },

    /**
     * @param {string} value
     */
    updateOtherText(value) {
      this.setProperties({
        'formData.reason_other': true,
        'formData.not_interested_text': value,
        'errors.not_interested_text': undefined,
        'errors.reason_other': undefined,
      });
    },

    /**
     * @param {Object} rateType
     */
    setRateType(rateType) {
      this.set('formData.suggested_rate_type', rateType.id);
    },

    setSuggestedRate(value) {
      this.set('formData.suggested_rate_amount', value);
    },

    /**
     *
     */
    submit() {
      if (this.submitting) {
        return;
      }

      const formData = { ...this.formData };
      const errors = { };

      if (formData['reason_other']) {
        if (!formData['not_interested_text']) {
          errors.not_interested_text = 'You must provide valid feedback';
        } else if (formData['not_interested_text'].length > 5000) {
          errors.not_interested_text = 'Your feedback must be less than 5000 characters';
        }
      }

      if(formData['reason_rate']) {
        if (!formData['suggested_rate_amount']) {
          errors.suggested_rate_amount = 'You must provide a valid suggested rate amount.';
        }
      } else {
        formData['suggested_rate_amount'] = null;
        formData['suggested_rate_type'] = null;
      }

      this.set('errors', errors);

      if (Object.keys(errors).length) {
        return;
      }

      if (!formData['not_interested_text']) {
        formData['not_interested_text'] = null;
      }

      this.set('submitting', true);
      this.submitFeedback(formData).catch((e) => {
        this.set('submitting', false);

        handleFormErrorOrModal(
          this,
          e,
          FORM_FIELDS,
          false,
          'There was an error sending your feedback. Please refresh and try again.',
          'sellerPostingFeedbackForm -- error sending user feedback' + JSON.stringify(e)
        );
      });
    },

    /**
     *
     */
    cancel() {
      this.get('close')();
    },
  }
});

export default SellerPostingFeedbackForm;
